/**
 * Path matching. Pure, and deliberately not a router library.
 *
 * Authority: bead scope-njw.
 *
 * The registry is generated, so the matcher only ever sees declared patterns —
 * `/staff/engagements/:engagementId` and nothing more exotic. Keeping it a pure
 * function of (patterns, pathname) is what lets the access decision above it be
 * tested without a DOM, a history, or a rendered tree.
 */
import type { RouteParams } from './types.ts'

/** `/a/b/` and `/a/b?x=1#y` are the same path as `/a/b`. */
export function normalisePath(pathname: string): string {
  const withoutQuery = pathname.split(/[?#]/, 1)[0] ?? ''
  const withLeading = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`
  const trimmed = withLeading.replace(/\/+$/, '')

  return trimmed.length === 0 ? '/' : trimmed
}

function segmentsOf(path: string): string[] {
  return normalisePath(path)
    .split('/')
    .filter((segment) => segment.length > 0)
}

/**
 * The parameters this pattern binds for this path, or null when it does not
 * match. An empty object is a match with no parameters — which is why the
 * failure value is null and not `{}`.
 */
export function matchPath(pattern: string, pathname: string): RouteParams | null {
  const patternSegments = segmentsOf(pattern)
  const pathSegments = segmentsOf(pathname)
  if (patternSegments.length !== pathSegments.length) return null

  const params: Record<string, string> = {}

  for (const [index, patternSegment] of patternSegments.entries()) {
    const pathSegment = pathSegments[index] ?? ''

    if (!patternSegment.startsWith(':')) {
      if (patternSegment !== pathSegment) return null
      continue
    }

    // An empty parameter is not a value. `/staff/engagements/` must not bind
    // engagementId to the empty string and hand it to a fetch.
    if (pathSegment.length === 0) return null
    params[patternSegment.slice(1)] = safeDecode(pathSegment)
  }

  return params
}

function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/** Static segments beat parameters, so `/staff/new` wins over `/staff/:id`. */
function specificity(pattern: string): number {
  return segmentsOf(pattern).filter((segment) => !segment.startsWith(':')).length
}

export interface RouteMatch<R> {
  readonly route: R
  readonly params: RouteParams
}

/**
 * The most specific route that matches, or null. Ties are impossible in a
 * generated registry — `assertRegistryIsSound` rejects two routes with the same
 * pattern before this ever runs — so first-past-the-post on specificity is a
 * total order, not a coin toss.
 */
export function matchRoute<R extends { readonly path: string }>(
  routes: readonly R[],
  pathname: string,
): RouteMatch<R> | null {
  let best: RouteMatch<R> | null = null
  let bestScore = -1

  for (const route of routes) {
    const params = matchPath(route.path, pathname)
    if (params === null) continue

    const score = specificity(route.path)
    if (score > bestScore) {
      best = { route, params }
      bestScore = score
    }
  }

  return best
}
